import { useEffect, useRef, useState } from "react";
import Title from "../../components/common/title/Title";
import { deleteApi, getApi } from "../../services/api/globalApi";
import { buildPublicFileUrl, fileUrl } from "../../services/config";
import ShowImageModal from "../../components/common/modals/show-image-modal/ShowImageModal";
import ShowVideoModal from "./ShowVideoModal";
import Swal from 'sweetalert2';
import { PiTrashBold } from "react-icons/pi";

export default function FileList() {
  const [fileData, setFileData] = useState([]);
  const [searchFile, setSearchFile] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [videoPath, setVideoPath] = useState(""); // New state for video path
  const imageRef = useRef();
  const videoRef = useRef(); // Ref for video modal

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const latestRequest = useRef(0);

  const getFiles = async (page = currentPage, search = searchFile) => {
    const request = ++latestRequest.current;
    const path = `${fileUrl}files`;
    try {
      const data = await getApi({
        path,
        params: { page, limit: itemsPerPage, search, variant: "original" },
      });
      if (request !== latestRequest.current) return;
      setFileData(data?.data?.files || []);
      setTotalPages(data?.data?.pagination?.totalPages || 1);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => getFiles(currentPage, searchFile), 350);
    return () => { latestRequest.current++; clearTimeout(timeout); };
  }, [currentPage, searchFile]);

  const showImage = (img) => {
    setImagePath(img);
    imageRef.current.showModal();
  };

  const showVideo = (videoName) => {
    setVideoPath(buildPublicFileUrl(videoName));
    videoRef.current.showModal(); // Show the video modal
  };

  const clearImagePath = () => {
    setImagePath(null);
  };

  const clearVideoPath = () => {
    setVideoPath(""); // Clear video path
  };

  const copyToClipboard = (item) => {
    navigator.clipboard.writeText(buildPublicFileUrl(item)).then(() => {
      alert("لینک به کلیپ‌بورد کپی شد!");
    }).catch((err) => {
      console.error('خطا در کپی کردن لینک:', err);
    });
  };
  const removeFile = async (name) => {
    const result = await Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: 'این فایل حذف خواهد شد!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'بله',
      cancelButtonText: 'خیر',
    });

    if (result.isConfirmed) {
      const path = `${fileUrl}file/${encodeURIComponent(name)}`;
      try {
        await deleteApi({ path });
        await getFiles(currentPage, searchFile);
        Swal.fire('حذف شد!', 'فایل با موفقیت حذف شد.', 'success');
      } catch (error) {
        console.log(error);
        Swal.fire('خطا', 'در حذف فایل مشکلی پیش آمد.', 'error');
      }
    }
  };
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <Title title={"لیست فایل ها"} />
      <ShowImageModal
        ref={imageRef}
        imagePath={imagePath}
        clearImagePath={clearImagePath}
      />
      <ShowVideoModal
        ref={videoRef}
        videoPath={videoPath}
        clearVideoPath={clearVideoPath}
      />
      <div className="max-w-md mx-auto my-2">
        <label
          htmlFor="default-search"
          className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
        >
          Search
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            onChange={(e) => {
              setSearchFile(e.target.value);
              setCurrentPage(1);
            }}
            type="search"
            className="bg-white text-center sm:text-right border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="جست و جو فایل ..."
            required
          />
        </div>
      </div>

      <ul className="divide-y divide-gray-200 dark:divide-gray-700 mt-8">
        {fileData?.map((item, index) => {
          const isImage = item.mimeType?.startsWith("image/") || item.name.match(/\.(jpg|jpeg|png|gif|webp|svg|heic)$/i);
          const isVideo = item.mimeType?.startsWith("video/") || item.name.match(/\.(mp4|avi|mov|webm)$/i);

          return (
            <li key={item._id || item.name || index} className="py-3">
              <div className="flex items-center space-x-4 rtl:space-x-reverse">
                {isImage && (
                  <div className="flex-shrink-0">
                    <img
                      className="w-8 h-8 rounded-full"
                      src={buildPublicFileUrl(item, { variant: "thumb_" })}
                      onError={(event) => {
                        if (!event.currentTarget.dataset.fallback) {
                          event.currentTarget.dataset.fallback = "true";
                          event.currentTarget.src = buildPublicFileUrl(item);
                        }
                      }}
                      loading="lazy"
                      alt=""
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                    {item.name}
                  </p>
                </div>
                {isImage ? (
                  <button
                    onClick={() => showImage(item?.name)}
                    type="button"
                    className="focus:outline-none text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-md text-sm px-4 py-1.5 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-900"
                  >
                    نمایش
                  </button>
                ) : isVideo ? (
                  <button
                    onClick={() => showVideo(item?.name)}
                    type="button"
                    className="focus:outline-none text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-md text-sm px-4 py-1.5 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-900"
                  >
                    نمایش ویدئو
                  </button>
                ) : (
                  <a
                    href={buildPublicFileUrl(item)}
                    download
                    className="focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-md text-sm px-4 py-1.5 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                  >
                    دانلود
                  </a>
                )}
                <button
                  onClick={() => copyToClipboard(item)}
                  type="button"
                  className="focus:outline-none text-white bg-gray-700 hover:bg-gray-800 focus:ring-4 focus:ring-gray-300 font-medium rounded-md text-sm px-4 py-1.5 dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800"
                >
                  کپی لینک
                </button>
                <PiTrashBold
                  onClick={() => removeFile(item.name)}
                  className="text-red-400 transition-all hover:text-red-600 cursor-pointer"

                />
              </div>
            </li>
          );
        })}
      </ul>

      {/* Pagination controls */}
      <div className="flex justify-between mt-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="text-gray-500 disabled:opacity-50"
        >
          قبلی
        </button>
        <span>
          صفحه {currentPage} از {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="text-gray-500 disabled:opacity-50"
        >
          بعدی
        </button>
      </div>
    </div>
  );
}
